import { Button, Layout, LoadingDots } from '@loadsmart/loadsmart-ui'
import mapping from 'atoms/Icon/mapping'
import Drawer from '@mui/material/Drawer'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import { PartnershipDetail } from 'common/types/kraken-core/Partnership'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useCreatePartnership, useGetPartnership, useUpdatePartnership } from '../api'
import { PartnershipForm, InstallationCard, ConnectionSettingsCard } from '../components'
import { useTabTitle } from 'hooks/useTabTitle/useTabTitle'
import { Stack, Typography, CircularProgress, Grid } from '@mui/material'
import { getInstallationsByPartnership } from 'services/kraken-core/installations/installations.service'
import { searchConnectionSettings } from 'services/kraken-core/connection_settings/connection_settings.service'
import { ConnectionSetting } from 'common/types/kraken-core/ConnectionSettings'
import { useQuery } from 'react-query'

export interface PartnershipDetailProps {
  id?: number
  open: boolean
  onClose: () => void
}

const NEW_EMPTY_PARTNERSHIP: PartnershipDetail = {}

export default function PartnershipDetailComponent({ open, id, onClose }: PartnershipDetailProps) {
  useTabTitle('Partnership')
  const isEdit = !!id
  const { data, isLoading } = useGetPartnership({ id: id as any })
  const [form, setForm] = useState<PartnershipDetail>(NEW_EMPTY_PARTNERSHIP)
  const {
    mutate: createPartnership,
    error: createError,
    isSuccess: createSuccess,
  } = useCreatePartnership()
  const {
    mutate: updatePartnership,
    error: updateError,
    isSuccess: updateSuccess,
  } = useUpdatePartnership()

  // Installations query
  const {
    data: installationsData,
    isLoading: isLoadingInstallations,
    error: installationsError,
  } = useQuery(
    ['installations-by-partnership', data?.id],
    () => (data?.id ? getInstallationsByPartnership(data.id) : Promise.resolve(undefined)),
    { enabled: !!data?.id }
  )

  // Connections query (for both party and counterparty)
  const {
    data: partyConnections,
    isLoading: isLoadingPartyConnections,
    error: partyConnectionsError,
  } = useQuery(
    ['connections-by-party', data?.party?.id],
    () =>
      data?.party?.id
        ? searchConnectionSettings({ ...({ owner: data.party.id } as any) })
        : Promise.resolve(undefined),
    { enabled: !!data?.party?.id }
  )
  const {
    data: counterpartyConnections,
    isLoading: isLoadingCounterpartyConnections,
    error: counterpartyConnectionsError,
  } = useQuery(
    ['connections-by-counterparty', data?.counterparty?.id],
    () =>
      data?.counterparty?.id
        ? searchConnectionSettings({ ...({ owner: data.counterparty.id } as any) })
        : Promise.resolve(undefined),
    { enabled: !!data?.counterparty?.id }
  )

  // Merge and deduplicate connections
  const connections = [
    ...(partyConnections?.response?.data?.results || []),
    ...(counterpartyConnections?.response?.data?.results || []),
  ].filter((item, idx, arr) => arr.findIndex(i => i.id === item.id) === idx)

  // For owner name lookup in connections
  // Helper to resolve owner name for a connection
  const getOwnerName = (conn: ConnectionSetting) => {
    if (!conn?.owner) return ''

    // Handle both string and object owner types
    const ownerId = typeof conn.owner === 'string' ? conn.owner : conn.owner?.id
    const ownerName = typeof conn.owner === 'string' ? '' : conn.owner?.name

    if (form.party?.id === ownerId) {
      return form.party?.name || ownerName || 'Unknown'
    }
    if (form.counterparty?.id === ownerId) {
      return form.counterparty?.name || ownerName || 'Unknown'
    }

    // If no match found, return the owner name if available, otherwise the ID
    return ownerName || String(ownerId) || 'Unknown'
  }

  // Update form state when API data changes (edit mode) or when switching to create
  useEffect(() => {
    if (!isLoading && data) {
      setForm(data)
    } else if (!id) {
      setForm(NEW_EMPTY_PARTNERSHIP)
    }
  }, [data, isLoading, id])

  const handleSave = () => {
    if (isEdit) {
      updatePartnership({
        ...form,
        counterparty: form.counterparty?.id,
        party: form.party?.id,
      })
    } else {
      createPartnership({
        ...form,
        counterparty: form.counterparty?.id,
        party: form.party?.id,
      })
    }
  }

  const handleFormUpdate = (updated: PartnershipDetail) => setForm(updated)

  useEffect(() => {
    const error = updateError || createError
    if (error) toast.error(`Something went wrong: ${error}`)
  }, [updateError, createError])

  useEffect(() => {
    const success = createSuccess || updateSuccess
    if (success) {
      toast.success(`Successfully saved`)
      onClose()
    }
  }, [updateSuccess, createSuccess])

  console.log('render')

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: '85vw', maxWidth: '85vw' } }}
    >
      <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {!isLoading ? (
            <>
              <PartnershipForm partnership={form} onUpdate={handleFormUpdate} isEdit={isEdit} />
              {/* Installations and Connections Sections - Side by Side */}
              <Stack spacing={2} mt={4}>
                <Grid container spacing={3}>
                  {/* Installations Section */}
                  <Grid item xs={12} md={8}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {mapping['Installation']}
                      <Typography variant="h5" fontWeight="bold">
                        Installations
                      </Typography>
                    </Box>
                    {isLoadingInstallations ? (
                      <Box display="flex" justifyContent="center" py={2}>
                        <CircularProgress />
                      </Box>
                    ) : installationsError ? (
                      <Typography color="error">{String(installationsError)}</Typography>
                    ) : !installationsData?.response?.data?.results?.length ? (
                      <Typography color="text.secondary">
                        No installations found for this partnership.
                      </Typography>
                    ) : (
                      <Stack spacing={2} mt={1}>
                        {installationsData.response.data.results.map(inst => (
                          <InstallationCard key={inst.id} installation={inst} />
                        ))}
                      </Stack>
                    )}
                  </Grid>
                  {/* Connections Section */}
                  <Grid item xs={12} md={4}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {mapping['ConnectionSettings']}
                      <Typography variant="h5" fontWeight="bold">
                        Connections
                      </Typography>
                    </Box>
                    {isLoadingPartyConnections || isLoadingCounterpartyConnections ? (
                      <Box display="flex" justifyContent="center" py={2}>
                        <CircularProgress />
                      </Box>
                    ) : partyConnectionsError || counterpartyConnectionsError ? (
                      <Typography color="error">
                        {String(partyConnectionsError || counterpartyConnectionsError)}
                      </Typography>
                    ) : !connections.length ? (
                      <Typography color="text.secondary">
                        No connections found for this partnership.
                      </Typography>
                    ) : (
                      <Stack spacing={2} mt={1}>
                        {connections.map(conn => (
                          <ConnectionSettingsCard
                            key={conn.id}
                            connection={conn}
                            getOwnerName={getOwnerName}
                          />
                        ))}
                      </Stack>
                    )}
                  </Grid>
                </Grid>
              </Stack>
            </>
          ) : (
            <LoadingDots />
          )}
        </Box>
        <Divider sx={{ mt: 2, mb: 2 }} />
        <Box>
          <Layout.Group className="w-full" align="center" justify="flex-end">
            <Button name="cancel" onClick={onClose}>
              Cancel
            </Button>
            <Button name="save" variant="primary" onClick={handleSave}>
              Save
            </Button>
          </Layout.Group>
        </Box>
      </Box>
    </Drawer>
  )
}
